/*
  AutocompleteString.jsx
*/

// Npm.
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';

// Components.
import TextField from './TextField';
import List from './List';
import Chip from './Chip';

// Hooks.
import useId from '../hooks/useId';

// Makes.
import { makeStyles, cx } from '../makes/makeStyles';

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    display: 'inline-flex',
    position: 'relative',
    verticalAlign: 'middle',
    flexDirection: 'column',
    color: theme.color_text_primary,
    '&.fullWidth': {
      display: 'flex',
      width: '100%',
    },
    '& > .options': {
      position: 'fixed',  // Stops overflow.
      zIndex: 10,
      minWidth: 200,
      maxHeight: 256,
      backgroundColor: theme.color_paper,
      boxShadow: theme.shadow(3),
      borderRadius: 4,
      overflowY: 'auto',
    },
    '& > .inputContainer': {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      borderRadius: '4px',
      boxShadow: `0 0 0 1px ${theme.color_divider} inset`,
      padding: theme.space(0.5, 1, 1, 0.5),
      cursor: 'text',
      '& > *': {
        margin: theme.space(0.5, 0, 0, 0.5),
      },
      '&.small > input': {
        fontSize: '0.875rem',
        padding: theme.space(0.5, 1),
      },
      '& > input': {
        flexGrow: 1,
        flexBasis: '128px',
        border: 'none',
        background: 'none',
        outline: 'none',
        minWidth: 0,
        width: '100%',
        fontSize: '1rem',
        fontWeight: 500,
        color: 'currentColor',
        '&::placeholder': {
          color: 'currentColor',
          opacity: 0.2,
        },
      },
    },
    '&:hover > .inputContainer': {
      boxShadow: `0 0 0 1px ${theme.color_text_secondary} inset`,
    },
    '&.focussed > label': {
      color: theme.color_primary_main,
    },
    '&.focussed > .inputContainer': {
      boxShadow: `0 0 0 2px ${theme.color_primary_main} inset`,
    },
    '&.disabled': {
      color: theme.color_text_disabled,
    },
    '&.disabled > .inputContainer': {
      boxShadow: `0 0 0 1px ${theme.color_divider} inset`,
      cursor: 'inherit',
    },
  },
}));

// Event handlers.
function handleStopPropagation(event) {
  event.stopPropagation();
}

/**
 * A text field that provides a dropdown of autocomplete options.
 * 
 * XXX: Not coded for single values at the moment!!
 */
export default function AutocompleteString({
  className,
  size,
  label,
  value,
  options,
  onChange,
  onInputChange,
  onKeyDown,
  onFocus,
  onBlur,
  autoFocus,
  minLength,
  maxLength,
  placeholder = 'Select...',
  getOptionLabel = (opt) => String(opt),
  getOptionValue = (opt) => String(opt),
  getOptionIcon,
  fullWidth = false,
  multiple = false,
  creatable = false,
  disabled = false,
  disableCloseOnSelect = false,
  ...rest
}) {

  const id = useId();
  const inputId = `${id}-input`;
  const inputContainerRef = useRef();
  const inputRef = useRef();

  // Parse value.

  const useValue = useMemo(() => {
    return multiple ? (Array.isArray(value) ? value : []) : (typeof value === 'string' ? value : null);
  }, [value, multiple]);

  // Option map for lookup of selected values.

  const optionMap = useMemo(() => {
    const map = new Map();
    if (options) {
      for (const opt of options) {
        map.set(getOptionValue(opt), opt);
      }
    }
    return map;
  }, [options]);

  // State.

  const [localValue, setLocalValue] = useState(() => {
    if (typeof useValue === 'string') {
      const opt = optionMap.get(useValue);
      return opt ? getOptionLabel(opt) : '';
    }
    return '';
  });

  const [focusStyle, setFocusStyle] = useState(null);

  // Filter options by local value, and by what is already selected.
  // NOTE: "Value" array assumed to contain object values, not the objects themselves.

  const useOptions = useMemo(() => {
    const valueSet = new Set(Array.isArray(useValue) ? useValue : (typeof useValue === 'string' ? [useValue] : []));
    const lc = localValue.toLowerCase();
    return (options || []).filter(opt =>
      !valueSet.has(getOptionValue(opt))
      && getOptionLabel(opt).toLowerCase().includes(lc)
    );
  }, [localValue, useValue, options]);

  // Function to update the list dropdown position.
  // This has to be done semi-frequently to make sure it is correctly positioned.

  const updateDropdownPosition = useCallback(() => {

    // TODO: Position ABOVE the input if it makes the dropdown fit better.

    if (inputContainerRef.current) {
      const box = inputContainerRef.current.getBoundingClientRect();
      setFocusStyle({ top: box.bottom, left: box.left, width: box.width });
    }
  }, [setFocusStyle]);

  // Effect to keep the hovering List element attached to the Autocomplete.

  const isFocussed = Boolean(focusStyle);
  const isOpen = isFocussed && useOptions.length > 0 && !disabled;

  useEffect(() => {
    if (isOpen) {

      function handleResize() {
        updateDropdownPosition();
      }

      window.addEventListener('resize', handleResize, false);
      window.addEventListener('scroll', handleResize, false);

      return () => {
        window.removeEventListener('resize', handleResize, false);
        window.removeEventListener('scroll', handleResize, false);
      };

    }
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
    }
  }, [isOpen, localValue, useValue]);

  // Focus handlers and effects.

  function handleFocusInput(event) {
    if (onFocus) {
      onFocus(event);
    }
    updateDropdownPosition();
  }

  function handleBlurInput(event) {
    setFocusStyle(null);
    setLocalValue('');
    if (onBlur) {
      onBlur(event);
    }
  }

  useEffect(() => {
    if (disabled && inputRef.current) {
      inputRef.current.blur();
    }
  }, [disabled]);

  useEffect(() => {
    if (inputRef.current) {
      if (isFocussed) {
        inputRef.current.focus();
      } else {
        inputRef.current.blur();
      }
    }
  }, [isFocussed]);

  // Input and change handlers.

  function addValue(next, type) {
    setLocalValue('');
    if (onChange) {
      onChange(Array.isArray(useValue) ? useValue.concat(next) : next, type);
    }
  }

  function handleChangeInput(event) {
    setLocalValue(event.target.value);
    if (onInputChange) {
      onInputChange(event);
    }
  }

  function handleKeyDownInput(event) {
    if (event.key === 'Enter') {
      if (useOptions.length === 1) {
        
        // If there is only one option, assume the user is selecting it.
        addValue(getOptionValue(useOptions[0]), 'enter');

      } else if (creatable) {

        // XXX: Not tested. Needs to account for above logic.
        addValue(localValue, 'enter');

      }
    } else if (event.key === 'Escape') {
      
      event.preventDefault();  // Stops dialogs from closing sneakily.
      event.currentTarget.blur();

    } else if (event.key === 'Backspace') {

      // Remove the last item from the array.
      if (
        Array.isArray(useValue)
        && useValue.length > 0
        && localValue.length === 0
        && onChange
      ) {
        onChange(useValue.slice(0, useValue.length - 1), 'backspace');
      }

    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  }

  function handleMouseDown(event) {
    if (!disabled) {
      event.preventDefault();
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }

  function handleClickOption(event, val, option) {
    if (!disableCloseOnSelect) {
      setFocusStyle(null);
    }
    addValue(val, 'select');
  }

  // Get root class.

  let rootClass = 'Autocomplete';
  if (fullWidth) {
    rootClass += ' fullWidth';
  }
  if (isFocussed) {
    rootClass += ' focussed';
  }
  if (disabled) {
    rootClass += ' disabled';
  }
  if (multiple) {
    rootClass += ' multiple';
  }
  if (size === 'small') {
    rootClass += ` ${size}`;
  }

  const classes = useStyles();
  const cls = useMemo(() => cx(rootClass, classes.root, className), [rootClass, classes.root, className]);

  // TODO: Allow keyboard focus and arrow for the list options.

  return (
    <div className={cls} onMouseDown={handleMouseDown}>
      {
        Boolean(label) &&
        <label className="caption" htmlFor={inputId}>{ label }</label>
      }
      <div ref={inputContainerRef} className="inputContainer">
        {
          Array.isArray(useValue) && useValue.map(val => {

            const opt = optionMap.get(val);
            const label = opt ? getOptionLabel(opt) : val;

            function handleClickDelete() {
              if (onChange) {
                onChange(useValue.filter(v => v !== val));
              }
            }

            return (
              <Chip
                key={val}
                icon={typeof getOptionIcon === 'function' ? getOptionIcon(opt) : null}
                label={label}
                disabled={disabled}
                onDelete={handleClickDelete}
                onMouseDownDelete={handleStopPropagation}
              />
            );
          })
        }
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          placeholder={placeholder}
          value={localValue}
          disabled={disabled}
          autoFocus={autoFocus}
          minLength={minLength}
          maxLength={maxLength}
          onChange={handleChangeInput}
          onKeyDown={handleKeyDownInput}
          onFocus={handleFocusInput}
          onBlur={handleBlurInput}
        />
      </div>
      {
        isOpen &&
        <div
          className="options"
          style={focusStyle}
        >
          <List
            options={useOptions}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            getOptionIcon={getOptionIcon}
            onClickOption={handleClickOption}
          />
        </div>
      }
    </div>
  );
}
